import { ChevronDown, Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import type { BackendMenu } from '../../services/api'
import {
  buildNavTree,
  flattenLeaves,
  foldersContainingPath,
  searchNavTree,
  type NavFolder,
  type NavLeaf,
} from './build-nav-tree'
import { iconFor } from './menu-icons'
import { slugify, testId } from '../../utils/test-id'

const OPEN_FOLDERS_KEY = 'wicend.erp.sidebar_folders'

function readOpenFolders(): string[] {
  try {
    const raw = localStorage.getItem(OPEN_FOLDERS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((code) => typeof code === 'string') : []
  } catch {
    return []
  }
}

const linkClass = (isActive: boolean, expanded: boolean, nested = false) =>
  `group relative flex items-center gap-3 rounded-md py-2.5 text-sm font-semibold transition ${
    expanded ? (nested ? 'pl-9 pr-3' : 'px-3') : 'justify-center px-0'
  } ${
    isActive
      ? 'bg-white text-wicend-800 shadow-sm'
      : 'text-teal-50/80 hover:bg-white/10 hover:text-white'
  }`

function Tooltip({ label }: { label: string }) {
  return (
    <div className="pointer-events-none absolute left-full ml-3 z-50 whitespace-nowrap rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white opacity-0 shadow-xl transition-[opacity,transform] duration-150 scale-95 group-hover:scale-100 group-hover:opacity-100">
      {label}
    </div>
  )
}

function LeafLink({
  leaf,
  expanded,
  nested,
  activePath,
}: {
  leaf: NavLeaf
  expanded: boolean
  nested?: boolean
  activePath?: string
}) {
  const Icon = iconFor(leaf.icon)
  const isCurrentActive = activePath ? activePath === leaf.path : false

  return (
    <NavLink
      to={leaf.path}
      data-testid={testId('shell-nav', slugify(leaf.code), 'link')}
      title={!expanded ? leaf.label : undefined}
      className={({ isActive }) =>
        linkClass(activePath ? isCurrentActive : isActive, expanded, nested)
      }
    >
      <Icon size={18} className="shrink-0" />
      {expanded && <span className="truncate">{leaf.label}</span>}
      {!expanded && <Tooltip label={leaf.label} />}
    </NavLink>
  )
}

export function SidebarNav({
  menus,
  hasPermission,
  expanded,
  onRequestExpand,
}: {
  menus: BackendMenu[]
  hasPermission: (permission: string) => boolean
  /** False when the sidebar is in icon-only mode. */
  expanded: boolean
  /** Opens the sidebar so the search field becomes usable. */
  onRequestExpand: () => void
}) {
  const location = useLocation()
  const [query, setQuery] = useState('')
  const [openFolders, setOpenFolders] = useState<string[]>(readOpenFolders)
  const searchRef = useRef<HTMLInputElement>(null)
  const shouldFocusSearch = useRef(false)

  const sections = useMemo(() => buildNavTree(menus, hasPermission), [menus, hasPermission])
  const hits = useMemo(() => searchNavTree(sections, query), [sections, query])
  const allLeaves = useMemo(() => flattenLeaves(sections), [sections])

  // Select the longest (most specific) menu path that matches the current pathname.
  // This avoids prefix collisions (e.g. "/app/bank-file" and "/app/bank-file/categories"
  // both highlighting when visiting the categories sub-path).
  const activePath = useMemo(() => {
    const pathname = location.pathname
    const matches = allLeaves.filter(
      (leaf) =>
        leaf.path !== '#' &&
        (pathname === leaf.path || pathname.startsWith(leaf.path + '/')),
    )
    if (matches.length === 0) return ''
    matches.sort((a, b) => b.path.length - a.path.length)
    return matches[0].path
  }, [allLeaves, location.pathname])

  // The folder holding the current page opens itself, so a deep link never
  // lands the user on a screen whose menu entry is hidden inside a closed folder.
  useEffect(() => {
    const active = foldersContainingPath(sections, location.pathname)
    if (active.length === 0) return
    setOpenFolders((current) => {
      const missing = active.filter((code) => !current.includes(code))
      return missing.length === 0 ? current : [...current, ...missing]
    })
  }, [sections, location.pathname])

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_FOLDERS_KEY, JSON.stringify(openFolders))
    } catch {
      // Storage disabled: folder state simply resets next visit.
    }
  }, [openFolders])

  useEffect(() => {
    if (expanded && shouldFocusSearch.current) {
      shouldFocusSearch.current = false
      searchRef.current?.focus()
    }
  }, [expanded])

  const toggleFolder = (code: string) => {
    setOpenFolders((current) =>
      current.includes(code) ? current.filter((item) => item !== code) : [...current, code],
    )
  }

  const renderFolder = (folder: NavFolder) => {
    // Icon-only mode has no room for disclosure, so folders flatten into their
    // items rather than becoming unreachable.
    if (!expanded) {
      return folder.items.map((leaf) => (
        <LeafLink key={leaf.code} leaf={leaf} expanded={false} activePath={activePath} />
      ))
    }

    const isOpen = openFolders.includes(folder.code)
    const FolderIcon = iconFor(folder.icon)

    return (
      <div key={folder.code}>
        <button
          type="button"
          onClick={() => toggleFolder(folder.code)}
          data-testid={testId('shell-nav-group', slugify(folder.code), 'toggle')}
          aria-expanded={isOpen}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-teal-50/80 transition hover:bg-white/10 hover:text-white"
        >
          <FolderIcon size={18} className="shrink-0" />
          <span className="flex-1 truncate text-left">{folder.label}</span>
          <ChevronDown
            size={15}
            className={`shrink-0 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
            aria-hidden
          />
        </button>
        {isOpen && (
          <div className="mt-1 space-y-1 border-l border-white/10 pl-2">
            {folder.items.map((leaf) => (
              <LeafLink
                key={leaf.code}
                leaf={leaf}
                expanded
                nested
                activePath={activePath}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <nav
      aria-label="Navigasi utama"
      data-testid="shell-nav"
      className="wicend-scrollbar flex-1 overflow-y-auto px-3 py-5"
    >
      {expanded ? (
        <div className="relative mb-5">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-teal-200/60"
            aria-hidden
          />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setQuery('')
            }}
            placeholder="Cari menu…"
            aria-label="Cari menu"
            data-testid="shell-nav-search-input"
            className="w-full rounded-md border border-white/10 bg-white/5 py-2 pl-9 pr-8 text-sm font-semibold text-white placeholder:text-teal-100/40 focus-visible:outline-2 focus-visible:outline-teal-300 focus-visible:outline-offset-1 [&::-webkit-search-cancel-button]:hidden"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Hapus pencarian"
              data-testid="shell-nav-search-clear-btn"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-teal-100/60 hover:bg-white/10 hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            shouldFocusSearch.current = true
            onRequestExpand()
          }}
          aria-label="Cari menu"
          data-testid="shell-nav-search-open-btn"
          className="mb-5 flex w-full items-center justify-center rounded-md py-2.5 text-teal-50/80 transition hover:bg-white/10 hover:text-white"
        >
          <Search size={18} />
        </button>
      )}

      {query ? (
        <div className="space-y-1">
          {hits.length === 0 && (
            <p className="px-3 py-6 text-center text-xs text-teal-100/50" data-testid="shell-nav-search-empty">
              Menu tidak ditemukan.
            </p>
          )}
          {hits.map(({ leaf, trail }) => {
            const Icon = iconFor(leaf.icon)
            return (
              <NavLink
                key={leaf.code}
                to={leaf.path}
                data-testid={testId('shell-nav-search-hit', slugify(leaf.code), 'link')}
                onClick={() => setQuery('')}
                className={({ isActive }) =>
                  linkClass(activePath ? activePath === leaf.path : isActive, true)
                }
              >
                <Icon size={18} className="shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{leaf.label}</span>
                  <span className="block truncate text-[10px] font-bold uppercase tracking-wide opacity-60">
                    {trail.join(' · ')}
                  </span>
                </span>
              </NavLink>
            )
          })}
        </div>
      ) : (
        <div className="space-y-6">
          {sections.map((section) => (
            <div key={section.code}>
              {expanded ? (
                <p className="mb-2 px-3 text-[10px] font-black uppercase tracking-[0.2em] text-teal-200/60">
                  {section.title}
                </p>
              ) : (
                <div className="mx-auto mb-2 h-px w-8 bg-white/10" />
              )}
              <div className="space-y-1">
                {section.nodes.map((node) =>
                  node.kind === 'item' ? (
                    <LeafLink
                      key={node.code}
                      leaf={node}
                      expanded={expanded}
                      activePath={activePath}
                    />
                  ) : (
                    renderFolder(node)
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </nav>
  )
}
